/**
 * Call-sheet review — a NEW 1a-native screen.
 *
 * The 1a design has no surface for this. Its documents screen offers
 * Confirm/Dismiss on LLM equipment suggestions, which is a different
 * mechanism entirely: those are proposals with a confidence score, and these
 * are deterministic bindings with an evidence locator. So this is design
 * work inside an existing visual system, not a transcription.
 *
 * TWO STATE MACHINES, NEVER MERGED:
 *
 *   PARSER RESOLUTION   deterministic | absent | refuse
 *   HUMAN DECISION      pending | accepted | edited | rejected
 *
 * They answer different questions and an `absent` fact is `pending` forever,
 * because there is nothing to confirm. One combined chip would make that read
 * as work outstanding.
 *
 * THE REFUSAL IS THE CASE THIS SCREEN EXISTS FOR. Sunshine's wrap has a
 * candidate — 2030 — that the parser found and declined to bind. It is
 * rendered at a DELIBERATELY LOWER hierarchy than a selected value, is not
 * formatted as a time, and has no confirm button: accepting it would be a
 * person AUTHORING a value, which is a different act from accepting a parse.
 *
 * THE TARGET UNIT-DAY IS CHOSEN, NEVER DERIVED. The document's unit wording
 * is documentary — "2ND UNIT CALL SHEET" is a string, not an identity — and
 * nothing here maps it to a `production_unit`. The picker sends an explicit
 * `shoot_day_id` every time, including when only one day is plausible: 060
 * exists because two units routinely share a date, and a date-matched
 * confirmation would overwrite the other unit's call.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { canWrite } from '../../api/client';
import {
  modeller, unitLabel,
  type DaySuggestion, type ExtractedFact, type Production, type ShootDay,
} from '../api';
import { PageHeader } from '../shell/Shell';
import { Empty, ErrorState, Loading } from '../shell/states';
import { Evidence, Fact, Note, Tag } from '../shell/provenance';
import './review.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDate(iso: string): string {
  const p = iso.split('-');
  return `${Number(p[2])} ${MONTHS[Number(p[1]) - 1] ?? p[1]} ${p[0]}`;
}

/** `08:30:00` -> `08:30`. Never reformatted beyond dropping seconds. */
function hhmm(t: string | null | undefined): string {
  return t ? t.slice(0, 5) : '';
}

/** The name attached to a confirmation. The server has no user records, so
 *  this is a person typing who they are — kept between visits because being
 *  asked once per fact would make it noise nobody reads. */
const WHO_KEY = 'equoia.confirmed_by';

function readWho(): string {
  try { return localStorage.getItem(WHO_KEY) ?? ''; } catch { return ''; }
}

/** What the PARSER did. Grey for absent — an absence is not a caution. */
function ResolutionTag({ status }: { status: ExtractedFact['status'] }) {
  if (status === 'deterministic') return <Tag tone="identity">Deterministic</Tag>;
  if (status === 'absent') return <Tag tone="muted">Not in document</Tag>;
  if (status === 'refuse') return <Tag tone="caution">Unresolved</Tag>;
  return <Tag tone="muted">No fact row</Tag>;
}

/** What a PERSON did. Separate row, separate label — never fused above. */
function DecisionTag({ fact }: { fact: ExtractedFact }) {
  if (fact.status !== 'deterministic') return null;
  switch (fact.confirmation_status) {
    case 'accepted': return <Tag tone="confirmed">Confirmed</Tag>;
    case 'edited': return <Tag tone="confirmed">Confirmed — edited</Tag>;
    case 'rejected': return <Tag tone="muted">Rejected</Tag>;
    default: return <Tag tone="assumed">Awaiting confirmation</Tag>;
  }
}

function FactBlock(
  { label, fact, doc, target, who, onConfirmed }: {
    label: string;
    fact: ExtractedFact;
    doc: DaySuggestion;
    target: ShootDay | null;
    who: string;
    onConfirmed: () => void;
  },
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending =
    fact.status === 'deterministic' && fact.confirmation_status === 'pending';
  /* Three separate reasons the action cannot proceed, and the button is only
     rendered when none of them apply. A control that exists and refuses makes
     the product look broken rather than unfinished. */
  const confirmable = pending && canWrite() && target !== null && who.trim() !== '';

  async function confirm() {
    if (!fact.fact_id || !target) return;
    setBusy(true);
    setError(null);
    try {
      await modeller.confirmFact(fact.fact_id, {
        shoot_day_id: target.id,
        decision: 'accepted',
        confirmed_by: who.trim(),
        /* Omitted deliberately: the parser's value IS the accepted value, and
           re-sending it from the client would make the client an authority on
           what the document said. */
      });
      onConfirmed();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className={`pem-fact pem-fact--${fact.status ?? 'none'}`}>
      <div className="pem-fact__head">
        <span className="pem-label">{label}</span>
        <div className="pem-fact__tags">
          <ResolutionTag status={fact.status} />
          <DecisionTag fact={fact} />
        </div>
      </div>

      {fact.status === 'deterministic' && (
        <div className="pem-fact__value">{hhmm(fact.value)}</div>
      )}

      {fact.status === 'absent' && (
        <p className="pem-fact__note">{fact.resolution_note}</p>
      )}

      {fact.status === 'refuse' && (
        <>
          <p className="pem-fact__note">
            The parser found candidate material and did not bind it.
          </p>
          {fact.candidates.length > 0 && (
            /* Lower hierarchy, on purpose: small, monospace, and NOT
               formatted as a time. `2030` is a string the document contains,
               not a value anything selected. */
            <div className="pem-fact__candidates">
              <span className="pem-provenance">
                Candidate{fact.candidates.length > 1 ? 's' : ''} found
              </span>
              <span className="pem-fact__candidate">{fact.candidates.join(', ')}</span>
            </div>
          )}
          <Note>{fact.resolution_note}</Note>
        </>
      )}

      <div className="pem-fact__actions">
        {confirmable && (
          <button type="button" className="pem-btn" disabled={busy} onClick={() => void confirm()}>
            {busy
              ? 'Confirming'
              : `Confirm ${hhmm(fact.value)} to ${unitLabel(target)}`}
          </button>
        )}
        {/* Pending, but something is missing. Say which thing — not a
            disabled button that will not explain itself. */}
        {pending && !confirmable && (
          <span className="pem-provenance">
            {!canWrite()
              ? 'An editing key is required to confirm.'
              : target === null
                ? 'Choose the unit-day this belongs to first.'
                : 'Enter who is confirming, above.'}
          </span>
        )}
        {/* No confirm button for absent or refused. There is nothing to
            accept, and offering one would present a candidate as a parser
            suggestion. */}
        {fact.status === 'refuse' && (
          <span className="pem-provenance">
            Resolving this means authoring a value — not this path
          </span>
        )}
      </div>

      {error && <p className="pem-fact__error">{error}</p>}

      {fact.status === 'deterministic' && (
        <Evidence label="Evidence">
          <Fact term="Document">{doc.day_identifier_raw ?? '—'}</Fact>
          <Fact term="As printed">{fact.raw_value_text}</Fact>
          <Fact term="Locator">{doc.evidence_quote}</Fact>
          <Fact term="Page">{doc.page_no != null ? String(doc.page_no) : 'n/a (spreadsheet)'}</Fact>
          <Fact term="Confirmation">
            {fact.confirmation_id != null ? `#${fact.confirmation_id}` : 'none yet'}
          </Fact>
        </Evidence>
      )}
    </div>
  );
}

/**
 * Which unit-day receives this document's facts.
 *
 * Preselection is VISUAL ONLY, and only when exactly one unit-day shares the
 * document's date. The value sent is always the explicit id of whatever is
 * selected — the request carries no date and the server resolves nothing.
 */
function TargetPicker(
  { days, value, onChange, suggestionDate, rawUnitLabel }: {
    days: ShootDay[];
    value: number | null;
    onChange: (id: number | null) => void;
    suggestionDate: string | null;
    rawUnitLabel: string | null;
  },
) {
  const sameDate = days.filter((d) => d.local_date === suggestionDate);
  const ambiguous = sameDate.length > 1;

  return (
    <div className="pem-target">
      <span className="pem-label">Confirm onto</span>
      {days.length === 0 ? (
        <Note>
          This production has no shooting days yet. A fact is confirmed onto a
          unit-day, so one has to exist first.
        </Note>
      ) : (
        <>
          <select
            className="pem-target__select"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">Choose a unit-day</option>
            {days.map((d) => (
              <option key={d.id} value={d.id}>
                {shortDate(d.local_date)} — {unitLabel(d)}
                {d.call_time_local ? ` · call ${hhmm(d.call_time_local)}` : ''}
              </option>
            ))}
          </select>
          {ambiguous && (
            /* The exact case 060 exists for. The date is not enough, it is
               not treated as enough, and the person is told why. */
            <Note>
              {sameDate.length} units share {shortDate(suggestionDate as string)}.
              The date does not choose between them.
            </Note>
          )}
          {rawUnitLabel && (
            <div className="pem-target__evidence">
              <span className="pem-provenance">Unit as the document names it</span>
              <b>{rawUnitLabel}</b>
              <Note>
                Documentary wording, shown to help you choose. It is not
                matched to a unit automatically.
              </Note>
            </div>
          )}
        </>
      )}
    </div>
  );
}

type State =
  | { t: 'loading' }
  | { t: 'error'; e: unknown }
  | { t: 'ok'; days: DaySuggestion[]; production: Production };

export function CallSheetReview() {
  const { id = '', slug = '' } = useParams();
  const [state, setState] = useState<State>({ t: 'loading' });
  const [who, setWho] = useState(readWho);
  const [targets, setTargets] = useState<Record<number, number | null>>({});

  const load = useCallback(async () => {
    const [days, production] = await Promise.all([
      modeller.daySuggestions(Number(id)),
      modeller.production(slug),
    ]);
    return { days, production };
  }, [id, slug]);

  const refresh = useCallback(() => {
    load()
      .then(({ days, production }) => setState({ t: 'ok', days, production }))
      .catch((e) => setState({ t: 'error', e }));
  }, [load]);

  useEffect(() => {
    let live = true;
    setState({ t: 'loading' });
    load()
      .then(({ days, production }) => { if (live) setState({ t: 'ok', days, production }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, [load]);

  /* Preselect only where there is genuinely one candidate. Runs when the data
     lands, and never overwrites a choice already made. */
  useEffect(() => {
    if (state.t !== 'ok') return;
    setTargets((current) => {
      const next = { ...current };
      let changed = false;
      for (const d of state.days) {
        if (next[d.id] !== undefined) continue;
        const same = state.production.shoot_days
          .filter((s) => s.local_date === d.local_date);
        next[d.id] = same.length === 1 ? (same[0] as ShootDay).id : null;
        changed = true;
      }
      return changed ? next : current;
    });
  }, [state]);

  function rememberWho(v: string) {
    setWho(v);
    try { localStorage.setItem(WHO_KEY, v); } catch { /* storage blocked */ }
  }

  if (state.t === 'loading') return <><PageHeader title="Call-sheet review" /><Loading what="the extraction" /></>;
  if (state.t === 'error') return <><PageHeader title="Call-sheet review" /><ErrorState what="the extraction" error={state.e} /></>;
  if (!state.days.length) {
    return (
      <>
        <PageHeader title="Call-sheet review" />
        <Empty title="Nothing extracted" body="This extraction produced no shoot-day suggestions." />
      </>
    );
  }

  const { production } = state;
  const anyPending = state.days.some((d) =>
    (d.crew_call.status === 'deterministic' && d.crew_call.confirmation_status === 'pending')
    || (d.estimated_wrap.status === 'deterministic'
        && d.estimated_wrap.confirmation_status === 'pending'));

  return (
    <>
      <PageHeader
        title="Call-sheet review"
        count={`extraction ${id}`}
        actions={
          <Link className="pem-link" to={`/modeller/productions/${production.slug}`}>
            ← {production.name}
          </Link>
        }
      />

      {/* Asked once for the whole screen rather than per fact. Only rendered
          when there is something confirmable and a key that could confirm it. */}
      {canWrite() && anyPending && (
        <label className="pem-field pem-review__who">
          <span className="pem-label">Confirmed by</span>
          <input value={who} onChange={(e) => rememberWho(e.target.value)}
                 placeholder="your name" />
          <span className="pem-provenance">
            Recorded on every confirmation you make, permanently.
          </span>
        </label>
      )}

      {state.days.map((d) => {
        const targetId = targets[d.id] ?? null;
        const target = production.shoot_days.find((s) => s.id === targetId) ?? null;
        return (
          <section key={d.id} className="pem-review">
            <header className="pem-review__head">
              <div>
                <h2>{d.local_date ?? 'Undated'}</h2>
                <div className="pem-review__ids">
                  {/* The day as PRINTED. 'P1 of 32' is a pre-shoot and is not
                      day 1; day_number stays null and the raw wording is what
                      the document actually said. */}
                  {d.day_identifier_raw && <span>Day identifier <b>{d.day_identifier_raw}</b></span>}
                  {d.day_number != null && <span>Day number <b>{d.day_number}</b></span>}
                </div>
              </div>
              <div className="pem-review__unit">
                <span className="pem-label">Unit as named</span>
                {d.raw_unit_label
                  ? <b>{d.raw_unit_label}</b>
                  : (
                    /* NULL means the document established no operating unit
                       identity. It does NOT mean the sheet printed
                       'UNIT CALL: N/A', which is a timing field. */
                    <>
                      <b className="pem-review__nounit">No unit named</b>
                      <Note>
                        The document establishes no operating unit identity.
                        Mapping it to a production unit is a human decision.
                      </Note>
                    </>
                  )}
              </div>
            </header>

            <TargetPicker
              days={production.shoot_days}
              value={targetId}
              onChange={(v) => setTargets((t) => ({ ...t, [d.id]: v }))}
              suggestionDate={d.local_date}
              rawUnitLabel={d.raw_unit_label}
            />

            <div className="pem-facts">
              <FactBlock label="Crew call" fact={d.crew_call} doc={d}
                         target={target} who={who} onConfirmed={refresh} />
              <FactBlock label="Estimated wrap" fact={d.estimated_wrap} doc={d}
                         target={target} who={who} onConfirmed={refresh} />
            </div>
          </section>
        );
      })}

      <div className="pem-detail-foot">
        <Note>
          Confirming a fact writes it to the unit-day you choose. Nothing here
          chooses one for you — two units routinely share a date.
        </Note>
      </div>
    </>
  );
}
